import { useState } from "react";

import Button from "@musicfy/components/Button";
import Divider from "@musicfy/components/Divider";
import PageLoader from "@musicfy/components/PageLoader";
import {
  SubscriptionPlanInfo,
  type AvailablePlans,
} from "@musicfy/contants/Subscriptions";
import { usePaddleContext } from "@musicfy/libs/PaddleProvider";
import { usePaddleUpgrade } from "@musicfy/libs/PaddleProvider/hooks/usePaddleUpgrade";
import { formatPrice, getLocale } from "@musicfy/utils";
import { StripePlanChangeKind } from "@musicfy/utils/stripePlanChange";

import FullScreenWrapper from "./shared/FullScreenWrapper";

const planLabel = (plan: AvailablePlans | string): string => {
  if (plan in SubscriptionPlanInfo) {
    return SubscriptionPlanInfo[plan as AvailablePlans].label;
  }
  return String(plan);
};

const StripeUpgrade = (): JSX.Element => {
  const { closeUpgrade, confirmStripeChange } = usePaddleUpgrade();
  const {
    upgradePreviewContext: { stripeChangePreview },
  } = usePaddleContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await confirmStripeChange();
    } finally {
      setIsLoading(false);
    }
  };

  if (!stripeChangePreview) {
    return (
      <FullScreenWrapper
        className="h-full w-full backdrop-blur-xl"
        open
        onClose={closeUpgrade}
        style="transparent"
        isLoading
      >
        <PageLoader />
      </FullScreenWrapper>
    );
  }

  const {
    kind,
    currentPlan,
    targetPlan,
    targetInterval,
    effectiveAt,
    amountDueTodayCents,
    recurringAmountCents,
    currency,
  } = stripeChangePreview;

  const locale = getLocale();
  const effectiveDate = effectiveAt
    ? new Date(effectiveAt).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const targetLabel = planLabel(targetPlan);
  const currentLabel = planLabel(currentPlan);

  const recurringPrice = formatPrice(recurringAmountCents / 100, currency);
  const dueToday = formatPrice(amountDueTodayCents / 100, currency);

  const isDowngrade = kind === StripePlanChangeKind.downgrade;

  return (
    <FullScreenWrapper
      className="h-full w-full backdrop-blur-xl"
      open
      onClose={closeUpgrade}
      style="transparent"
      isLoading={false}
    >
      <div className="p-9 relative flex items-center justify-center flex-grow">
        <div className="h-full w-full max-w-[436px] flex justify-center flex-col gap-7">
          <span className="text-38 font-bold">
            {isDowngrade ? "Confirm downgrade" : "Confirm upgrade"}
          </span>

          <div className="capitalize">
            <span className="text-38 font-bold">{recurringPrice}</span>
            <span className="text-pink-200 text-20">/{targetInterval}</span>
          </div>

          <div className="text-16 text-white/80 leading-relaxed">
            {isDowngrade ? (
              <>
                You&apos;ll keep{" "}
                <span className="font-bold">{currentLabel}</span> until{" "}
                <span className="font-bold">{effectiveDate}</span>. Then
                you&apos;ll move to{" "}
                <span className="font-bold">{targetLabel}</span>. No charge
                today.
              </>
            ) : (
              <>
                You&apos;ll be charged or credited the prorated difference
                today. Your <span className="font-bold">{targetLabel}</span>{" "}
                plan starts immediately.
              </>
            )}
          </div>

          <Divider className="bg-white/[20%]" />

          <div className="flex items-center justify-between text-14">
            <div className="text-white">Due Today</div>
            <div className="font-bold">{isDowngrade ? "$0.00" : dueToday}</div>
          </div>

          <Button
            className="text-20 !py-5"
            onClick={() => {
              void handleConfirm();
            }}
            loading={isLoading}
          >
            {isDowngrade ? "Schedule Downgrade" : "Upgrade Now"}
          </Button>
        </div>
      </div>
    </FullScreenWrapper>
  );
};

export default StripeUpgrade;
